import React from "react";
import { Text, View, StyleSheet } from "@react-pdf/renderer";
import Checkbox from "./checkbox";
import { withNamespaces } from "react-i18next";

const styles = StyleSheet.create({
  section: {
    marginHorizontal: 10,
    paddingHorizontal: 50,
    // Do not set height: "100%" or flexGrow: 1 on flowing content —
    // both force incorrect page-break calculations and blank pages.
  },
  sectionTitle: {
    fontWeight: 600,
    marginBottom: 10,
    fontSize: 13,
    lineHeight: 1.4,
  },
  subsectionTitle: {
    fontWeight: 600,
    fontSize: 11,
    lineHeight: 1.4,
    marginLeft: 10,
    marginTop: 10,
    marginBottom: 8,
  },
  table: {
    width: "100%",
    marginTop: 10,
    marginBottom: 10,
    borderStyle: "solid",
    borderWidth: 1,
    borderColor: "#000",
  },
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#000",
    borderBottomStyle: "solid",
  },
  tableLastRow: {
    borderBottomWidth: 0,
  },
  tableColHeader: {
    width: "50%",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-start",
    backgroundColor: "#fff",
    borderRightWidth: 1,
    borderRightColor: "#000",
    borderRightStyle: "solid",
  },
  fontBold: {
    fontWeight: 600,
  },
  tableCol: {
    width: "50%",
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "flex-start",
    borderRightWidth: 1,
    borderRightColor: "#000",
    borderRightStyle: "solid",
    paddingTop: 2,
    paddingBottom: 2,
  },
  // Seven equal columns for the subcontractors table
  subcontractorCol: {
    width: "14.28%",
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "flex-start",
    borderRightWidth: 1,
    borderRightColor: "#000",
    borderRightStyle: "solid",
    paddingTop: 2,
    paddingBottom: 2,
  },
  subcontractorColHeader: {
    width: "14.28%",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-start",
    backgroundColor: "#fff",
    borderRightWidth: 1,
    borderRightColor: "#000",
    borderRightStyle: "solid",
  },
  borderRightNone: {
    borderRightWidth: 0,
  },
  // Wrapper View owns flex sizing; Text must not use flexbox props
  // (flexDirection/alignItems/flexGrow on Text causes vertical overlap).
  tableCellWrapper: {
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: 0,
    margin: 5,
  },
  tableCell: {
    fontSize: 8,
    lineHeight: 1.4,
  },
  text: {
    fontSize: 10,
    lineHeight: 1.5,
    marginBottom: 10,
  },
  subsection: {
    marginBottom: 4,
  },
});

const MeasureCell = ({ measure, t, isLastColumn }) => (
  <View
    style={[
      styles.tableCol,
      isLastColumn ? styles.borderRightNone : null,
    ]}
  >
    {measure ? (
      <>
        <Checkbox isChecked={measure?.is_checked} />
        <View style={styles.tableCellWrapper}>
          <Text style={styles.tableCell}>{t(measure.title)}</Text>
        </View>
      </>
    ) : null}
  </View>
);

const PdfTable = ({ technicalMeasures, organizationalMeasures, t }) => {
    const longestListCount = Math.max(
      technicalMeasures?.length || 0,
      organizationalMeasures?.length || 0
    );

    const measures = [];

    for (let i = 0; i < longestListCount; i++) {
        const isLastRow = i + 1 >= longestListCount;
        const techMeasure = technicalMeasures[i];
        const orgMeasure = organizationalMeasures[i];

        measures.push(
            <View
              style={[styles.tableRow, isLastRow ? styles.tableLastRow : null]}
              key={i}
            >
                <MeasureCell measure={techMeasure} t={t} />
                <MeasureCell measure={orgMeasure} t={t} isLastColumn />
            </View>
        );
    }

    return (
        // wrap={false}: keep the table atomic — if it does not fit in the
        // remaining space, move the entire table to the next page.
        // Do NOT use `break` — that always forces a page break and creates
        // blank pages when paragraphs still have room below them.
        <View style={styles.table} wrap={false}>
            <View style={styles.tableRow}>
                <View style={styles.tableColHeader}>
                    <View style={styles.tableCellWrapper}>
                      <Text style={[styles.tableCell, styles.fontBold]}>
                        {t("Technical measures")}
                      </Text>
                    </View>
                </View>
                <View style={[styles.tableColHeader, styles.borderRightNone]}>
                    <View style={styles.tableCellWrapper}>
                      <Text style={[styles.tableCell, styles.fontBold]}>
                        {t("Organizational measures")}
                      </Text>
                    </View>
                </View>
            </View>

            {measures}
        </View>
    );
};

const PageContent = (props) => {
    const { sections, sub_contractors, t } = props;
    
    return (
        <View style={styles.section}>
            {sections.map((section, index) => {
                const subsections = section.sub_sections.filter((s) => s.is_show_on_pdf);
                
                return (
                    <View key={index}>
                        <Text
                          style={styles.sectionTitle}
                          minPresenceAhead={60}
                        >
                            {`${index + 1}. ${ t(section.title)}`}
                        </Text>

                        {subsections.map((subSection, subIndex) => {
                            const technicalMeasures = subSection.measures.filter(
                                (measure) => measure.type === "technical"
                            );

                            const organizationalMeasures = subSection.measures.filter(
                                (measure) => measure.type === "organizational"
                            );

                            return (
                                <View key={subIndex} style={styles.subsection}>
                                    <Text
                                      style={styles.subsectionTitle}
                                      minPresenceAhead={80}
                                    >
                                        {`${index + 1}.${ subIndex + 1 } ${t(subSection.title)}`}
                                    </Text>

                                    {!!subSection.information && (
                                      <Text style={styles.text}>
                                        {t(subSection.information)}
                                      </Text>
                                    )}

                                    <PdfTable
                                        technicalMeasures={technicalMeasures}
                                        organizationalMeasures={organizationalMeasures}
                                        t={t}
                                    />

                                    {!!subSection.description && (
                                      <Text style={styles.text}>
                                        {t(subSection.description)}
                                      </Text>
                                    )}
                                </View>
                            );
                        })}
                    </View>
                )
            })}

            <View>
                <Text style={styles.sectionTitle} minPresenceAhead={60}>
                    {`${sections?.length + 1}. ${t('List of subcontractors')}`}
                </Text>

                <View style={styles.table} wrap={false}>
                    <View style={styles.tableRow}>
                      <View style={styles.subcontractorColHeader}>
                          <View style={styles.tableCellWrapper}>
                            <Text style={[styles.tableCell, styles.fontBold]}>#</Text>
                          </View>
                      </View>
                      <View style={styles.subcontractorColHeader}>
                          <View style={styles.tableCellWrapper}>
                            <Text style={[styles.tableCell, styles.fontBold]}>{t("Area")}</Text>
                          </View>
                      </View>
                      <View style={styles.subcontractorColHeader}>
                          <View style={styles.tableCellWrapper}>
                            <Text style={[styles.tableCell, styles.fontBold]}>{t("Name")}</Text>
                          </View>
                      </View>
                      <View style={styles.subcontractorColHeader}>
                          <View style={styles.tableCellWrapper}>
                            <Text style={[styles.tableCell, styles.fontBold]}>{t("Website")}</Text>
                          </View>
                      </View>
                      <View style={styles.subcontractorColHeader}>
                          <View style={styles.tableCellWrapper}>
                            <Text style={[styles.tableCell, styles.fontBold]}>{t("GTC")}</Text>
                          </View>
                      </View>
                      <View style={styles.subcontractorColHeader}>
                          <View style={styles.tableCellWrapper}>
                            <Text style={[styles.tableCell, styles.fontBold]}>{t("Privacy Policy")}</Text>
                          </View>
                      </View>
                      <View style={[styles.subcontractorColHeader, styles.borderRightNone]}>
                          <View style={styles.tableCellWrapper}>
                            <Text style={[styles.tableCell, styles.fontBold]}>{t("Address")}</Text>
                          </View>
                      </View>
                    </View>
                    {sub_contractors.map((contractor, index) => {
                      const isLastRow = index + 1 >= sub_contractors?.length;

                      return (
                          <View
                            style={[styles.tableRow, isLastRow ? styles.tableLastRow : null]}
                            key={index}
                          >
                            <View style={styles.subcontractorCol}>
                                <View style={styles.tableCellWrapper}>
                                  <Text style={styles.tableCell}>{index + 1}</Text>
                                </View>
                            </View>
                            <View style={styles.subcontractorCol}>
                                <View style={styles.tableCellWrapper}>
                                  <Text style={styles.tableCell}>{t(contractor.area)}</Text>
                                </View>
                            </View>
                            <View style={styles.subcontractorCol}>
                                <View style={styles.tableCellWrapper}>
                                  <Text style={styles.tableCell}>{t(contractor.name)}</Text>
                                </View>
                            </View>
                            <View style={styles.subcontractorCol}>
                                <View style={styles.tableCellWrapper}>
                                  <Text style={styles.tableCell}>{contractor.website}</Text>
                                </View>
                            </View>
                            <View style={styles.subcontractorCol}>
                                <View style={styles.tableCellWrapper}>
                                  <Text style={styles.tableCell}>{t(contractor.g_t_c)}</Text>
                                </View>
                            </View>
                            <View style={styles.subcontractorCol}>
                                <View style={styles.tableCellWrapper}>
                                  <Text style={styles.tableCell}>
                                    {t(contractor.privacy_policy)}
                                  </Text>
                                </View>
                            </View>
                            <View style={[styles.subcontractorCol, styles.borderRightNone]}>
                                <View style={styles.tableCellWrapper}>
                                  <Text style={styles.tableCell}>{contractor.address}</Text>
                                </View>
                            </View>
                          </View>
                      );
                    })}
                </View>
            </View>
        </View>
    );
};

export default withNamespaces()(PageContent);
